import React, { useEffect, useRef, useState, useCallback } from "react";
import { Mandala, Lotus, BorderOrnament } from "./IndianMotifs";

const SLIDES = [
  {
    label: "Smile Makeover",
    before: "/img/ba-smile-before.jpg",
    after: "/img/ba-smile-after.jpg",
    note: "Veneers + whitening · 2 sessions",
    altBefore: "Patient's smile before veneers and whitening treatment",
    altAfter: "Patient's radiant smile after veneers and whitening makeover",
  },
  {
    label: "Teeth Whitening",
    before: "/img/ba-hydra-before.jpg",
    after: "/img/ba-hydra-after.jpg",
    note: "Professional bleaching · 6 shades brighter",
    altBefore: "Patient's teeth before professional whitening treatment",
    altAfter: "Patient's teeth 6 shades brighter after professional bleaching",
  },
  {
    label: "Aligners",
    before: "/img/ba-laser-before.jpg",
    after: "/img/ba-laser-after.jpg",
    note: "Clear aligners · 12-month transformation",
    altBefore: "Patient's teeth alignment before clear aligner treatment",
    altAfter: "Patient's straightened teeth after 12 months of clear aligners",
  },
];

export default function Results() {
  const [i, setI] = useState(0);
  const [pos, setPos] = useState(55);
  const wrap = useRef(null);
  const beforeImg = useRef(null);
  const afterImg = useRef(null);

  const prefersReducedMotion = typeof window !== "undefined"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;

  useEffect(() => {
    if (prefersReducedMotion) return; // WCAG 2.3.3
    const onScroll = () => {
      if (!wrap.current) return;
      const r = wrap.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = Math.max(-1, Math.min(1, (r.top + r.height / 2 - vh / 2) / vh));
      if (beforeImg.current) beforeImg.current.style.transform = `translateY(${-p * 36}px) scale(1.05)`;
      if (afterImg.current) afterImg.current.style.transform = `translateY(${-p * 20}px) scale(1.05)`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [prefersReducedMotion]);

  const next = useCallback(() => setI((v) => (v + 1) % SLIDES.length), []);
  const prev = useCallback(() => setI((v) => (v - 1 + SLIDES.length) % SLIDES.length), []);
  const slide = SLIDES[i];

  // WCAG 2.1.1: Keyboard controls for the slider
  const handleKeyDown = useCallback((e) => {
    if (e.key === "ArrowLeft") { setPos((p) => Math.max(0, p - 5)); e.preventDefault(); }
    if (e.key === "ArrowRight") { setPos((p) => Math.min(100, p + 5)); e.preventDefault(); }
    if (e.key === "ArrowUp") { prev(); e.preventDefault(); }
    if (e.key === "ArrowDown") { next(); e.preventDefault(); }
  }, [next, prev]);

  return (
    <section id="results" aria-label="Before and after treatment results" className="relative py-28 md:py-40 overflow-hidden">
      <Lotus size={160} className="absolute top-24 right-10 text-gold-500/15 hidden md:block" aria-hidden="true" />
      <Mandala size={320} className="absolute -left-20 bottom-10 text-sage-700/[0.06] hidden md:block" aria-hidden="true" />
      <div className="container mx-auto px-5 md:px-10">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="text-[11px] uppercase tracking-[0.32em] text-sage-700/80 mb-3">Results — 03</div>
            <h2 className="font-display text-5xl md:text-6xl xl:text-7xl leading-[1.02] tracking-tightest">
              Quietly <span className="font-script text-gold-500">undeniable.</span>
            </h2>
            <div className="mt-4 text-gold-600/70" aria-hidden="true"><BorderOrnament width={200} /></div>
          </div>
          <div className="hidden md:flex items-center gap-2" role="group" aria-label="Navigate results">
            <button onClick={prev} data-testid="results-prev" data-cursor="hover" aria-label="Previous result" className="w-12 h-12 rounded-full border border-sage-700/20 text-sage-800 hover:bg-sage-700 hover:text-cream transition-colors">←</button>
            <button onClick={next} data-testid="results-next" data-cursor="hover" aria-label="Next result" className="w-12 h-12 rounded-full border border-sage-700/20 text-sage-800 hover:bg-sage-700 hover:text-cream transition-colors">→</button>
          </div>
        </div>

        <div
          ref={wrap}
          className="relative aspect-[16/9] md:aspect-[21/9] w-full rounded-[36px] overflow-hidden ring-gold-soft select-none"
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            setPos(((e.clientX - r.left) / r.width) * 100);
          }}
          onTouchMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const t = e.touches[0];
            setPos(((t.clientX - r.left) / r.width) * 100);
          }}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="slider"
          aria-label={`Before and after comparison for ${slide.label}. Use left and right arrow keys to move the divider.`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pos)}
          aria-valuetext={`Divider at ${Math.round(pos)}% — showing ${Math.round(pos)}% before and ${Math.round(100 - pos)}% after`}
          data-testid="results-slider"
        >
          <img ref={beforeImg} src={slide.before} alt={slide.altBefore} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
            <img ref={afterImg} src={slide.after} alt={slide.altAfter} className="w-full h-full object-cover" />
          </div>

          <div className="absolute top-0 bottom-0" style={{ left: `${pos}%`, transform: "translateX(-50%)" }} aria-hidden="true">
            <div className="h-full w-px bg-cream/90" />
            <div className="absolute top-1/2 -translate-y-1/2 -left-5 w-10 h-10 rounded-full bg-cream grid place-items-center shadow-[0_0_24px_rgba(201,169,110,0.7)]">
              <span className="text-sage-900 text-xs">↔</span>
            </div>
          </div>

          <div className="absolute top-6 left-6 glass rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.28em] text-sage-900" aria-hidden="true">
            Before
          </div>
          <div className="absolute top-6 right-6 glass rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.28em] text-sage-900" aria-hidden="true">
            After
          </div>
          <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-cream">
            <div>
              <div className="font-display text-3xl md:text-4xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]">{slide.label}</div>
              <div className="text-xs uppercase tracking-[0.28em] opacity-90 mt-1">{slide.note}</div>
            </div>
            <div className="flex gap-2 md:hidden" role="group" aria-label="Navigate results on mobile">
              <button onClick={prev} aria-label="Previous result" className="w-10 h-10 rounded-full glass-dark text-cream">←</button>
              <button onClick={next} aria-label="Next result" className="w-10 h-10 rounded-full glass-dark text-cream">→</button>
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between text-xs uppercase tracking-[0.28em] text-sage-700/70">
          <span aria-live="polite">0{i + 1} <span className="text-sage-700/30 mx-2" aria-hidden="true">/</span> 0{SLIDES.length}</span>
          <span className="hidden md:inline">Drag the divider or use arrow keys to reveal the result</span>
          <span>{slide.label}</span>
        </div>
      </div>
    </section>
  );
}
